import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { ANIMAL_TYPES, ANIMAL_SEX } from '../../lib/animals';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  name: '',
  animalType: 'HORSE',
  subType: '',
  breed: '',
  age: '',
  sex: '',
  notes: '',
  handlingNotes: '',
  behaviorNotes: '',
  medicalNotes: '',
};

export default function AnimalDialog({ open, onOpenChange, animal, onSaved }) {
  const { token } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = !!animal?.id;

  useEffect(() => {
    if (open) {
      if (animal?.id) {
        setForm({
          name: animal.name || '',
          animalType: animal.animalType || 'HORSE',
          subType: animal.subType || '',
          breed: animal.breed || '',
          age: animal.age != null ? String(animal.age) : '',
          sex: animal.sex || '',
          notes: animal.notes || '',
          handlingNotes: animal.handlingNotes || '',
          behaviorNotes: animal.behaviorNotes || '',
          medicalNotes: animal.medicalNotes || '',
        });
      } else {
        setForm(EMPTY);
      }
    }
  }, [open, animal]);

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!form.animalType) {
      toast.error('Animal type is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        animalType: form.animalType,
        subType: form.subType.trim() || null,
        breed: form.breed.trim() || null,
        age: form.age === '' ? null : parseInt(form.age, 10),
        sex: form.sex || null,
        notes: form.notes.trim() || null,
        handlingNotes: form.handlingNotes.trim() || null,
        behaviorNotes: form.behaviorNotes.trim() || null,
        medicalNotes: form.medicalNotes.trim() || null,
      };
      const headers = { Authorization: `Bearer ${token}` };
      if (isEdit) {
        await axios.patch(`${API_URL}/api/client/animals/${animal.id}`, payload, { headers });
        toast.success('Animal updated');
      } else {
        await axios.post(`${API_URL}/api/client/animals`, payload, { headers });
        toast.success('Animal saved');
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save animal');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Animal' : 'Add Animal'}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground bg-muted/60 rounded p-2">
          These details are <strong>shared with providers</strong> you book. Providers may add
          their own private working notes (kicks, bites, etc.) that you won't see.
        </p>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={set('name')}
                placeholder="Bella"
                maxLength={200}
                data-testid="animal-name-input"
              />
            </div>
            <div>
              <Label>Type *</Label>
              <Select value={form.animalType} onValueChange={set('animalType')}>
                <SelectTrigger data-testid="animal-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ANIMAL_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Breed</Label>
              <Input
                value={form.breed}
                onChange={set('breed')}
                placeholder="Quarter Horse"
                maxLength={100}
              />
            </div>
            <div>
              <Label>Sub-type</Label>
              <Input
                value={form.subType}
                onChange={set('subType')}
                placeholder="e.g. Mini, Dairy"
                maxLength={100}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Age (years)</Label>
              <Input
                type="number"
                min={0}
                max={200}
                value={form.age}
                onChange={set('age')}
                placeholder="7"
              />
            </div>
            <div>
              <Label>Sex</Label>
              <Select value={form.sex} onValueChange={set('sex')}>
                <SelectTrigger>
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  {ANIMAL_SEX.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>General notes</Label>
            <Textarea
              rows={2}
              value={form.notes}
              onChange={set('notes')}
              placeholder="Anything providers should know"
              maxLength={2000}
            />
          </div>
          <div>
            <Label>Handling notes</Label>
            <Textarea
              rows={2}
              value={form.handlingNotes}
              onChange={set('handlingNotes')}
              placeholder="e.g. Easy to halter, ties well"
              maxLength={2000}
            />
          </div>
          <div>
            <Label>Behavior notes</Label>
            <Textarea
              rows={2}
              value={form.behaviorNotes}
              onChange={set('behaviorNotes')}
              placeholder="e.g. Spooks at plastic bags"
              maxLength={2000}
            />
          </div>
          <div>
            <Label>Medical notes</Label>
            <Textarea
              rows={2}
              value={form.medicalNotes}
              onChange={set('medicalNotes')}
              placeholder="Allergies, conditions, vet contact"
              maxLength={2000}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving} data-testid="animal-save-btn">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {isEdit ? 'Save Changes' : 'Add Animal'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
